import { addDays, format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { normalizeTiptapHtml } from '@/lib/richtext/normalizeTiptapHtml';
import { stripHtmlToText } from '@/lib/text';
import type { ProducerFleaMarketOccurrence } from '@/types/fleaMarkets';

export type FleaOccurrenceUiStatus = 'planned' | 'cancelled' | 'completed';

export function formatOccurrenceRangeSv(startAt: string, endAt?: string | null): string {
  const start = new Date(startAt);
  if (Number.isNaN(start.getTime())) return '';

  const day = format(start, 'EEE d MMM', { locale: sv });
  const startTime = format(start, 'HH:mm');

  if (!endAt) return `${day} kl ${startTime}`;

  const end = new Date(endAt);
  if (Number.isNaN(end.getTime())) return `${day} kl ${startTime}`;

  const sameDay = format(start, 'yyyy-MM-dd') === format(end, 'yyyy-MM-dd');
  if (sameDay) {
    return `${day} kl ${startTime}–${format(end, 'HH:mm')}`;
  }

  return `${day} kl ${startTime} – ${format(end, 'EEE d MMM HH:mm', { locale: sv })}`;
}

/** Short date only: "17 mars" */
export function formatOccurrenceDateShortSv(startAt: string): string {
  const start = new Date(startAt);
  if (Number.isNaN(start.getTime())) return '';
  return format(start, 'd MMMM', { locale: sv });
}

/** Time range only: "kl 10:00–14:00" */
export function formatOccurrenceTimeRangeSv(startAt: string, endAt?: string | null): string {
  const start = new Date(startAt);
  if (Number.isNaN(start.getTime())) return '';
  const startTime = format(start, 'HH:mm');
  if (!endAt) return `kl ${startTime}`;
  const end = new Date(endAt);
  if (Number.isNaN(end.getTime())) return `kl ${startTime}`;
  const sameDay = format(start, 'yyyy-MM-dd') === format(end, 'yyyy-MM-dd');
  if (sameDay) return `kl ${startTime}\u2013${format(end, 'HH:mm')}`;
  return `kl ${startTime}`;
}

export function getNextUpcomingOccurrence<T extends { start_at: string; end_at?: string | null; status?: string }>(
  occurrences: T[],
  now: Date = new Date(),
): T | null {
  return occurrences
    .filter((item) => getOccurrenceUiStatus(item, now) === 'planned')
    .filter((item) => {
      const end = item.end_at ? new Date(item.end_at) : new Date(item.start_at);
      return end.getTime() >= now.getTime();
    })
    .sort((a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime())[0] ?? null;
}

export function sortOccurrencesAscending(items: ProducerFleaMarketOccurrence[]): ProducerFleaMarketOccurrence[] {
  return [...items].sort((a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime());
}

export function getOccurrenceUiStatus(
  occurrence: { start_at: string; end_at?: string | null; status?: string },
  now: Date = new Date(),
): FleaOccurrenceUiStatus {
  // `completed` is a UI-derived/presentation status in v1 and should not be persisted
  // by frontend writes unless backend/domain model later supports it explicitly.
  if (occurrence.status === 'cancelled') return 'cancelled';
  if (occurrence.status === 'completed') return 'completed';

  const effectiveEnd = new Date(occurrence.end_at ?? occurrence.start_at);
  if (Number.isNaN(effectiveEnd.getTime())) return 'planned';
  return effectiveEnd.getTime() < now.getTime() ? 'completed' : 'planned';
}

export function getOccurrenceUiLabel(status: FleaOccurrenceUiStatus): string {
  if (status === 'cancelled') return 'Inställt';
  if (status === 'completed') return 'Genomfört';
  return 'Planerat';
}

export function sortOccurrencesNearestFirst(
  items: ProducerFleaMarketOccurrence[],
  now: Date = new Date(),
): ProducerFleaMarketOccurrence[] {
  // Intent: "närmast först" for upcoming/cancelled-by-date, while keeping completed (past)
  // at the end to avoid pushing old history above near-future rows.
  const rank: Record<FleaOccurrenceUiStatus, number> = {
    planned: 0,
    cancelled: 1,
    completed: 2,
  };

  return [...items].sort((left, right) => {
    const leftStatus = getOccurrenceUiStatus(left, now);
    const rightStatus = getOccurrenceUiStatus(right, now);
    const leftCompleted = leftStatus === 'completed';
    const rightCompleted = rightStatus === 'completed';
    if (leftCompleted !== rightCompleted) return leftCompleted ? 1 : -1;

    const leftStart = new Date(left.start_at).getTime();
    const rightStart = new Date(right.start_at).getTime();

    if (leftStart !== rightStart) {
      if (leftCompleted) return rightStart - leftStart;
      return leftStart - rightStart;
    }

    return rank[leftStatus] - rank[rightStatus];
  });
}

export function toIsoFromDateTimeLocal(value: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

export function toIsoFromDateAndTime(dateValue: string, timeValue: string): string | null {
  if (!dateValue || !timeValue) return null;
  return toIsoFromDateTimeLocal(`${dateValue}T${timeValue}`);
}

function normalizeImagesOverride(images?: string[] | null): string[] | null {
  const cleaned = (images ?? [])
    .map((image) => image.trim())
    .filter((image) => image.length > 0);
  return cleaned.length > 0 ? cleaned : null;
}

export function normalizeOccurrenceDescriptionOverride(value?: string | null): string | null {
  if (!value) return null;

  const normalizedHtml = normalizeTiptapHtml(value);
  if (normalizedHtml) return normalizedHtml;

  const plainText = stripHtmlToText(value);
  return plainText || null;
}

function normalizeOccurrenceNote(value?: string | null): string | null {
  const plainText = stripHtmlToText(value);
  return plainText || null;
}

export function resolveOccurrenceDescription(
  occurrence: Pick<ProducerFleaMarketOccurrence, 'description_override' | 'note'>,
  baseDescription: string | null,
): string | null {
  const override =
    normalizeOccurrenceDescriptionOverride(occurrence.description_override) ||
    occurrence.note?.trim() ||
    null;
  return override || baseDescription;
}

export function resolveOccurrenceImages(
  occurrence: Pick<ProducerFleaMarketOccurrence, 'images_override'>,
): string[] {
  return normalizeImagesOverride(occurrence.images_override) ?? [];
}

export function buildOccurrenceEditPatch(input: {
  startAtLocal?: string;
  date?: string;
  startTime?: string;
  endAtLocal?: string;
  endTime?: string;
  descriptionOverride?: string | null;
  imagesOverride?: string[] | null;
  note?: string | null;
}): {
  start_at: string;
  end_at: string | null;
  note: string | null;
  description_override: string | null;
  images_override?: string[] | null;
} | null {
  const startIso =
    toIsoFromDateTimeLocal(input.startAtLocal ?? '') ||
    toIsoFromDateAndTime(input.date ?? '', input.startTime ?? '');
  if (!startIso) return null;

  const endIso = input.endAtLocal
    ? toIsoFromDateTimeLocal(input.endAtLocal)
    : input.date && input.endTime
      ? toIsoFromDateAndTime(input.date, input.endTime)
      : null;
  if (endIso && new Date(endIso).getTime() < new Date(startIso).getTime()) return null;

  const descriptionOverride = normalizeOccurrenceDescriptionOverride(input.descriptionOverride);
  const includeImagesOverride = input.imagesOverride !== undefined;
  const normalizedImages = includeImagesOverride ? normalizeImagesOverride(input.imagesOverride) : null;
  const normalizedNote = normalizeOccurrenceNote(input.note) || normalizeOccurrenceNote(descriptionOverride);

  return {
    start_at: startIso,
    end_at: endIso,
    note: normalizedNote,
    description_override: descriptionOverride,
    ...(includeImagesOverride ? { images_override: normalizedImages } : {}),
  };
}

export interface FleaOccurrenceBulkInput {
  fleaMarketId: string;
  fromDate: string;
  toDate: string;
  weekdays: number[];
  startTime: string;
  endTime?: string;
  note?: string | null;
}

type FleaOccurrenceInsert = Omit<ProducerFleaMarketOccurrence, 'id' | 'created_at' | 'updated_at' | 'cancelled_at'>;

export function hasInvalidTimeRange(startTime: string, endTime?: string): boolean {
  if (!startTime || !endTime) return false;
  return endTime < startTime;
}

export function buildBulkOccurrencePayloads(input: FleaOccurrenceBulkInput): FleaOccurrenceInsert[] {
  if (!input.fromDate || !input.toDate || !input.startTime) return [];
  if (hasInvalidTimeRange(input.startTime, input.endTime)) return [];

  const from = new Date(`${input.fromDate}T00:00:00`);
  const to = new Date(`${input.toDate}T00:00:00`);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime()) || from.getTime() > to.getTime()) return [];

  const weekdaySet = new Set(input.weekdays.filter((day) => day >= 0 && day <= 6));
  if (weekdaySet.size === 0) return [];

  const rows: FleaOccurrenceInsert[] = [];

  for (let date = from; date.getTime() <= to.getTime(); date = addDays(date, 1)) {
    if (!weekdaySet.has(date.getDay())) continue;

    const day = format(date, 'yyyy-MM-dd');
    const startIso = toIsoFromDateTimeLocal(`${day}T${input.startTime}`);
    if (!startIso) continue;

    const endIso = input.endTime ? toIsoFromDateTimeLocal(`${day}T${input.endTime}`) : null;

    rows.push({
      flea_market_id: input.fleaMarketId,
      start_at: startIso,
      end_at: endIso,
      description_override: input.note?.trim() || null,
      images_override: null,
      note: input.note?.trim() || null,
      status: 'scheduled',
    });
  }

  return rows;
}

/**
 * Filters bulk payloads that would duplicate an existing occurrence on the same
 * calendar date (local). Two occurrences are considered duplicates when they fall
 * on the same `yyyy-MM-dd` day — sufficient for flea market scheduling where a
 * single occurrence per day is the expected model.
 */
export function filterBulkDuplicates<T extends { start_at: string }>(
  payloads: FleaOccurrenceInsert[],
  existing: T[],
): { payloads: FleaOccurrenceInsert[]; skippedCount: number } {
  const existingDates = new Set(
    existing.map((item) => {
      const start = new Date(item.start_at);
      if (Number.isNaN(start.getTime())) return '';
      return format(start, 'yyyy-MM-dd');
    }),
  );

  const filtered = payloads.filter((payload) => {
    const start = new Date(payload.start_at);
    if (Number.isNaN(start.getTime())) return true;
    return !existingDates.has(format(start, 'yyyy-MM-dd'));
  });

  return { payloads: filtered, skippedCount: payloads.length - filtered.length };
}
